import type { ReactNode } from 'react';
import { AppAdultingMarkTile } from '../../../ui-foundation';
import type { IntegrationSettingsRecord } from '@/lib/integration-settings';
import { getHomeRoutineReminderStyleLabel } from '../../../ui-foundation/features/notifications';

export const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05" />
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
  </svg>
);

export const AppleMark = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M14.26 9.55c-.02-2.29 1.87-3.4 1.96-3.45-1.07-1.57-2.74-1.78-3.33-1.81-1.42-.14-2.77.84-3.49.84-.72 0-1.83-.82-3.01-.8-1.54.02-2.97.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.77 1.11 1.68 2.35 2.88 2.31 1.16-.05 1.59-.75 2.99-.75 1.4 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.24.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.43-.93-2.45-3.68zM11.98 2.9c.64-.78 1.07-1.86.95-2.94-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.77-.97 2.82 1.02.08 2.06-.52 2.71-1.26z" fill="currentColor" />
  </svg>
);

export const AdultingMark = () => (
  <AppAdultingMarkTile tileSize={16} imageWidth={10} imageHeight={9} />
);

export function ProviderButtonLabel({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-center justify-center text-current">{icon}</span>
      <span>{label}</span>
    </span>
  );
}

export function formatCalendarSyncStatus(lastSyncedAt: string | null, syncStatus: string | null | undefined) {
  if (syncStatus === 'error') {
    return lastSyncedAt
      ? `Sync needs another try. Previous sync ${new Date(lastSyncedAt).toLocaleString('en-GB')}.`
      : 'Sync needs another try.';
  }

  if (!lastSyncedAt) {
    return 'Connected but not synced yet.';
  }

  return `Last synced ${new Date(lastSyncedAt).toLocaleString('en-GB')}.`;
}

export function formatPushStatus(settings: IntegrationSettingsRecord | null, loading: boolean) {
  if (loading) {
    return 'Loading alert settings…';
  }

  if (!settings?.push_notifications_enabled) {
    return 'Mobile push is off. Open the Adulting iPhone or Android app to turn it on for a device.';
  }

  if (settings.push_permission_status !== 'granted') {
    return 'Mobile push is marked on, but permission still needs approving in your phone settings.';
  }

  if (!settings.push_last_registered_at) {
    return 'Mobile push is on and waiting for the next device registration.';
  }

  return `Mobile push is on. Last registered ${new Date(settings.push_last_registered_at).toLocaleString('en-GB')}.`;
}

export function formatHomeRoutineReminderStyle(settings: IntegrationSettingsRecord | null, loading: boolean) {
  if (loading) {
    return 'Loading Home reminder style…';
  }

  return `${getHomeRoutineReminderStyleLabel(settings?.home_routine_reminder_strength || 'standard')} for Home routines`;
}
